import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, Timer } from "lucide-react";

const useCountdown = (targetAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetAt]);
  if (!targetAt) return null;
  const target = new Date(targetAt).getTime();
  const diff = target - now;
  if (diff <= 0) return { over: true, target, diff };
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);
  return { over: false, target, diff, days, hours, mins, secs };
};

// Compact unit block used across the strip.
const Unit = ({ value, label, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center min-w-[52px]">
    <div className="font-heading text-2xl md:text-3xl font-black tabular-nums leading-none text-white">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-400 mt-1">{label}</div>
  </div>
);

export default function SeasonCountdownBanner() {
  const [cd, setCd] = useState(null);
  useEffect(() => {
    api.get("/season/countdown").then((r) => setCd(r.data)).catch(() => setCd(null));
  }, []);
  const t = useCountdown(cd?.target_at);
  if (!cd?.target_at || !t) return null;

  // Once past the lock time, keep the bar for 48h with a "SEASON LOCKED" state.
  const past48h = t.over && Math.abs(t.diff) > 48 * 3600e3;
  if (past48h) return null;

  const tone = t.over
    ? "border-[#F4D03F] bg-[#F4D03F]/10"
    : t.days < 1 ? "border-[#FF007F] bg-[#FF007F]/5" : "border-cyan-400 bg-cyan-400/5";
  const accent = t.over ? "text-[#F4D03F]" : (t.days < 1 ? "text-[#FF007F]" : "text-cyan-400");

  return (
    <div
      data-testid="season-countdown-banner"
      className={`border ${tone} px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-4 md:gap-6`}
    >
      <div className="flex items-center gap-3">
        {t.over ? <Lock className={`w-5 h-5 ${accent}`} /> : <Timer className={`w-5 h-5 ${accent}`} />}
        <div>
          <div className={`text-[10px] font-mono uppercase tracking-[0.3em] ${accent}`}>
            {t.over ? "Season locked" : cd.label}
          </div>
          <div className="text-sm text-gray-300 mt-0.5">
            {t.over
              ? `Locked ${new Date(cd.target_at).toLocaleString()} · rosters frozen · reach an admin for exceptions`
              : cd.message}
          </div>
        </div>
      </div>
      {!t.over && (
        <div className="flex items-end gap-3 md:ml-auto">
          <Unit value={t.days}  label="days"  testid="cd-days"  />
          <div className={`${accent} text-2xl font-mono pb-4 select-none`}>:</div>
          <Unit value={t.hours} label="hours" testid="cd-hours" />
          <div className={`${accent} text-2xl font-mono pb-4 select-none`}>:</div>
          <Unit value={t.mins}  label="mins"  testid="cd-mins"  />
          <div className={`${accent} text-2xl font-mono pb-4 select-none`}>:</div>
          <Unit value={t.secs}  label="secs"  testid="cd-secs"  />
        </div>
      )}
      <Link
        to="/my-team"
        data-testid="cd-cta"
        className={`text-[10px] font-mono uppercase tracking-widest border px-3 py-2 font-bold ${
          t.over
            ? "border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black"
            : "border-white text-white hover:bg-white hover:text-black"
        }`}
      >
        {t.over ? "View team →" : "Trim roster →"}
      </Link>
    </div>
  );
}
